import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Link } from 'react-router-dom';

/**
 * School List Component
 *
 * Displays a searchable, paginated list of SMK (Sekolah Menengah Kejuruan)
 * with filtering capabilities and status indicators.
 *
 * Features:
 * - Search by school name, NPSN, or district (kecamatan)
 * - Filter by documentation, KJP, and KIP status
 * - Pagination (20 items per page)
 * - Click to view school details
 * - Status badges for quick visual reference
 *
 * @author AI Agent (OpenClaw)
 * @date February 18, 2026
 */

const SchoolList = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDocStatus, setSelectedDocStatus] = useState('');
  const [selectedKjpStatus, setSelectedKjpStatus] = useState('');
  const [selectedKipStatus, setSelectedKipStatus] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Unique values for filters
  const [cities, setCities] = useState([]);

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('sekolah')
        .select('*', { count: 'exact' })
        .order('nama', { ascending: true });

      // Apply search filter
      if (searchQuery.trim()) {
        query = query.or(`nama.ilike.%${searchQuery}%,npsn.ilike.%${searchQuery}%,kecamatan.ilike.%${searchQuery}%`);
      }

      // Apply city filter
      if (selectedCity) {
        query = query.eq('kota', selectedCity);
      }

      // Apply documentation status filter
      if (selectedDocStatus) {
        query = query.eq('status_dokumentasi', selectedDocStatus);
      }

      // Apply KJP status filter
      if (selectedKjpStatus) {
        query = query.eq('status_kjp', selectedKjpStatus);
      }

      // Apply KIP status filter
      if (selectedKipStatus) {
        query = query.eq('status_kip', selectedKipStatus);
      }

      // Apply pagination
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      const { data, error, count } = await query.range(from, to);

      if (error) throw error;

      setSchools(data || []);
      setTotalCount(count || 0);

      // Fetch unique cities for filter dropdown
      if (cities.length === 0) {
        const { data: cityData } = await supabase
          .from('sekolah')
          .select('kota')
          .not('kota', 'is', null)
          .order('kota');

        if (cityData) {
          const uniqueCities = [...new Set(cityData.map(s => s.kota))];
          setCities(uniqueCities.sort());
        }
      }
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError('Gagal memuat data sekolah. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, selectedCity, selectedDocStatus, selectedKjpStatus, selectedKipStatus]);

  // Fetch schools on mount and when filters change
  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  // Reset filters
  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedCity('');
    setSelectedDocStatus('');
    setSelectedKjpStatus('');
    setSelectedKipStatus('');
    setCurrentPage(1);
  };

  // Status badge helper
  const getStatusBadge = (status, type = 'default') => {
    const styles = {
      doc: {
        belum_upload: 'bg-gray-100 text-gray-800',
        selesai_upload: 'bg-green-100 text-green-800'
      },
      kjp: {
        belum_upload: 'bg-gray-100 text-gray-800',
        sudah_upload: 'bg-green-100 text-green-800'
      },
      kip: {
        belum_upload: 'bg-gray-100 text-gray-800',
        sudah_upload: 'bg-green-100 text-green-800'
      }
    };

    const statusStyles = styles[type] || styles.doc;
    const styleClass = statusStyles[status] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styleClass}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);

  if (loading && schools.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Daftar Sekolah Menengah Kejuruan</h1>
          <p className="mt-2 text-gray-600">
            Jelajahi daftar SMK dengan status dokumentasi, KJP, dan KIP
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Total: {totalCount} sekolah
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cari Sekolah
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Nama, NPSN, atau kecamatan..."
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              />
            </div>

            {/* City Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kota
              </label>
              <select
                value={selectedCity}
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              >
                <option value="">Semua Kota</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            {/* Documentation Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status Dokumentasi
              </label>
              <select
                value={selectedDocStatus}
                onChange={(e) => {
                  setSelectedDocStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              >
                <option value="">Semua Status</option>
                <option value="belum_upload">Belum Upload</option>
                <option value="selesai_upload">Selesai Upload</option>
              </select>
            </div>

            {/* KJP Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status KJP
              </label>
              <select
                value={selectedKjpStatus}
                onChange={(e) => {
                  setSelectedKjpStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              >
                <option value="">Semua Status</option>
                <option value="belum_upload">Belum Upload</option>
                <option value="sudah_upload">Sudah Upload</option>
              </select>
            </div>

            {/* KIP Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status KIP
              </label>
              <select
                value={selectedKipStatus}
                onChange={(e) => {
                  setSelectedKipStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              >
                <option value="">Semua Status</option>
                <option value="belum_upload">Belum Upload</option>
                <option value="sudah_upload">Sudah Upload</option>
              </select>
            </div>

            {/* Reset Button */}
            <div className="flex items-end">
              <button
                onClick={handleResetFilters}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Reset Filter
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* School List */}
        {schools.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchQuery || selectedCity || selectedDocStatus || selectedKjpStatus || selectedKipStatus
                ? 'Tidak ada sekolah yang cocok dengan filter yang dipilih.'
                : 'Belum ada data sekolah.'}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nama Sekolah
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        NPSN
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kecamatan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status Dokumentasi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status KJP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status KIP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Aksi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {schools.map((school) => (
                      <tr key={school.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {school.nama}
                          </div>
                          {school.kota && (
                            <div className="text-sm text-gray-500">
                              {school.kota}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{school.npsn}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{school.kecamatan || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(school.status_dokumentasi, 'doc')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(school.status_kjp, 'kjp')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(school.status_kip, 'kip')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            to={`/school/${school.id}`}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Lihat Detail
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="bg-white shadow rounded-lg mt-4 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Menampilkan <span className="font-medium">{startIndex}</span> sampai{' '}
                  <span className="font-medium">{endIndex}</span> dari{' '}
                  <span className="font-medium">{totalCount}</span> sekolah
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border rounded-md text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>

                  <span className="text-sm text-gray-700">
                    Halaman {currentPage} dari {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border rounded-md text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolList;
